import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Center,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  createHistory,
  getHistories,
  getNewDictionaries,
  searchWorks,
} from "apis";
import { Suggestion, Word } from "utils/types";
import { apis } from "utils/config";
import {
  generateItemsPerPage,
  generateSuggestionUrl,
  getUser,
  setUser,
} from "utils/functions";
import { LIMIT_HISTORY, LIMIT_SEARCH } from "utils/constants";

import { Header, NewHistories, SearchResult } from "components";
import Definitions from "components/Definitions";
import AutoSuggestInput from "components/SuggestionInput";

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const pos = searchParams.get("pos") || "";

  const [userId, setUserId] = useState(getUser());
  const [filters, setFilters] = useState<string[]>([]);
  const [collocatePos, setCollocatePos] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isChangePage, setIsChangePage] = useState<boolean | undefined>(
    undefined
  );
  const [lang, setLang] = useState("en");

  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const itemsPerPage = generateItemsPerPage();
  const queryClient = useQueryClient();

  const {
    data: historyData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [apis.history(userId).key, userId, itemsPerPage],
    queryFn: () =>
      getHistories({
        userId: userId || "",
        query: {
          _limit: LIMIT_HISTORY,
        },
      }),
    enabled: !!userId,
    keepPreviousData: true,
  });

  const histories = useMemo(() => historyData?.data || [], [historyData?.data]);

  const { mutate } = useMutation({
    mutationFn: createHistory,
    onSuccess: () => {
      refetch();
    },
  });

  const { data, isFetching } = useQuery({
    queryKey: [
      apis.search().key,
      keyword,
      filters,
      collocatePos,
      pos,
      currentPage,
    ],
    queryFn: () =>
      searchWorks({
        keyword: keyword,
        filters: filters.join(","),
        collocate_pos: collocatePos.join(","),
        lemma_pos: pos,
        _limit: LIMIT_SEARCH,
        _page: currentPage,
      }),
    enabled: !!keyword,
    onSuccess: () => {
      if (typeof isChangePage !== "undefined" && !isChangePage) {
        mutate({
          userId: userId,
          data: {
            keyword: keyword,
            filters: filters.join(","),
            pos: collocatePos.join(","),
          },
        });
      }
    },
  });

  const { data: dictionaries, isFetching: isFetchingDictionaries } = useQuery({
    queryKey: [apis.newDictionaries().key, keyword, lang],
    queryFn: () => getNewDictionaries(keyword),
    enabled: !!keyword,
  });

  const handleSubmit = (suggestion: Suggestion) => {
    navigate(generateSuggestionUrl(suggestion));
    setCurrentPage(1);
    setFilters([]);
    setCollocatePos([]);
    setIsChangePage(false);
    setCurrentHistoryPage(1);
  };

  const handleSearch = useCallback(
    ({
      filters,
      collocatePos,
    }: {
      filters: string[];
      collocatePos: string[];
    }) => {
      setCurrentPage(1);
      setFilters(filters);
      setCollocatePos(collocatePos);
      setIsChangePage(false);
      setCurrentHistoryPage(1);
    },
    []
  );

  const handleChangePage = useCallback((page: number) => {
    setCurrentPage(page);
    setIsChangePage(true);
  }, []);

  const handleClear = useCallback(() => {
    const newUserId = uuidv4();
    setUserId(newUserId);
    setUser(newUserId);
    setCurrentHistoryPage(1);
    queryClient.invalidateQueries({
      queryKey: [apis.history(newUserId).key, newUserId, itemsPerPage],
    });
  }, [itemsPerPage, queryClient]);

  return (
    <>
      <Header />

      <Container maxW="container.lg">
        <Box mt="4" width={{ base: "100%", md: "300px" }}>
          <AutoSuggestInput
            defaultValue={keyword}
            onSubmit={handleSubmit}
            loading={isFetching}
          />
        </Box>
        <Box>
          <NewHistories
            histories={histories}
            keyword={keyword}
            itemsPerPage={itemsPerPage}
            isLoading={isLoading}
            currentPage={currentHistoryPage}
            onSubmit={handleSubmit}
            onClear={handleClear}
            onSetCurentPage={setCurrentHistoryPage}
          />
        </Box>
        {!!keyword ? (
          <Tabs mt="1" isFitted>
            <TabList>
              <Tab>
                <Text color="grayTitle" fontSize="xl">
                  Definitions
                </Text>
              </Tab>
              <Tab>
                <Text color="grayTitle" fontSize="xl">
                  Collocates
                </Text>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel py="1" px="0">
                <Definitions
                  data={dictionaries}
                  isLoading={isFetchingDictionaries}
                  language={lang}
                  onChangeLanguage={setLang}
                  keyword={keyword}
                  google={dictionaries?.google?.translations || []}
                  pos={data?.matched_pos?.[0]}
                  word={keyword}
                />
              </TabPanel>
              <TabPanel py="1" px="0">
                <SearchResult
                  data={{
                    word: keyword,
                    collocates: data?.data as Word[],
                    total: data?.total || 0,
                    matched_pos: data?.matched_pos || [],
                  }}
                  isLoading={isFetching}
                  currentPage={currentPage}
                  collocatePos={collocatePos}
                  filters={filters}
                  onSearch={handleSearch}
                  onChangePage={handleChangePage}
                  onSubmit={handleSubmit}
                  keyword={keyword}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          <Center my="8">
            <Text color="grayTitle" textAlign="center">
              Enter a word to search for its meaning and collocates
            </Text>
          </Center>
        )}
      </Container>
    </>
  );
};

export default Home;
